import { Helmet } from "react-helmet-async";

const BASE_URL = "https://cosx.co";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

// Article-specific OG / Twitter metadata. Rendered only for og:type=article
// pages (i.e. blog posts), where crawlers use these for rich link previews
// and social-card facts.
export interface ArticleMeta {
  publishedTime?: string;     // ISO-8601 publish date — e.g. article.date
  modifiedTime?: string;      // ISO-8601 last update
  author?: string;            // human-readable author name shown on the article
  section?: string;           // category, e.g. "Product", "Company"
  tags?: string[];            // optional taxonomy tags
  // Twitter card "label/data" pairs — render as facts under the share preview.
  twitterLabels?: Array<{ label: string; data: string }>;
}

export function PageMeta({
  title,
  description,
  path,
  ogImage,
  ogType,
  noindex,
  article,
}: {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  article?: ArticleMeta;
}) {
  const url = `${BASE_URL}${path}`;
  const image = ogImage ?? DEFAULT_OG_IMAGE;
  const twitterLabels = article?.twitterLabels ?? [];
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogType ?? "website"} />
      <meta property="og:site_name" content="COSX" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />

      {/* article:* are read by Facebook, LinkedIn and Slack to enrich the
          link card. Google's article snippet uses JSON-LD, so we set both. */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {article?.tags?.map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {/* twitter:label/data pairs surface as small captions under the card */}
      {twitterLabels.slice(0, 2).map((pair, i) => (
        <>
          <meta key={`label-${i}`} name={`twitter:label${i + 1}`} content={pair.label} />
          <meta key={`data-${i}`} name={`twitter:data${i + 1}`} content={pair.data} />
        </>
      ))}
    </Helmet>
  );
}
